import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, FileText, MessageCircle, RotateCcw, Plus } from "lucide-react";
import api, { apiErr, downloadFile, API } from "@/lib/api";
import { StatusBadge } from "./Overview";
import DataTable from "@/components/app/DataTable";
import Modal from "@/components/app/Modal";
import { btn, btnOutline, btnPrimary, inr, pageSub, pageTitle, inputCls } from "@/components/app/ui";

const STATUSES = ["pending", "confirmed", "processing", "packed", "ready_for_pickup", "shipped", "out_for_delivery", "delivered", "cancelled", "refund_requested", "refunded"];
const pretty = (s) => (s || "").replace(/_/g, " ");
const paidState = (o) => o.payment_status || "pending";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get("/vendor/orders").then(({ data }) => setOrders(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  // /vendor/orders?order=<id> (from a customer page) opens that order
  const wanted = params.get("order");
  useEffect(() => {
    if (!wanted || !orders.length) return;
    const o = orders.find((x) => x.id === wanted);
    if (o) setSel(o);
    setParams({}, { replace: true });
  }, [wanted, orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/vendor/orders/${id}/status`, { status });
      toast.success(`Order marked ${pretty(status)}.`);
      load();
      setSel((s) => (s?.id === id ? { ...s, status } : s));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); load(); }
  };

  const refund = async (o) => {
    if (!window.confirm(`Refund ${inr(o.total)} for ${o.order_number}? Stock will be restored.`)) return;
    try {
      await api.post(`/vendor/orders/${o.id}/refund`, { reason: "Vendor refund", restock: true });
      toast.success(`Refund of ${inr(o.total)} processed for ${o.order_number}.`);
      setSel(null); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const whatsapp = async (o) => {
    try { const { data } = await api.get(`/vendor/orders/${o.id}/whatsapp`); window.open(data.link, "_blank"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const invoice = async (o) => {
    try { await downloadFile(`/vendor/orders/${o.id}/invoice`, `invoice-${o.order_number}.pdf`); toast.success("Invoice downloaded."); }
    catch { toast.error("Could not download the invoice. Please try again."); }
  };

  const columns = useMemo(() => [
    { key: "order_number", header: "Order", cell: (o) => <span className="font-mono font-semibold text-orange-600">{o.order_number}</span> },
    { key: "created_at", header: "Date", hideBelow: "md", accessor: (o) => o.created_at || "", cell: (o) => <span className="text-slate-600 whitespace-nowrap">{(o.created_at || "").slice(0, 10) || "—"}</span> },
    { key: "customer", header: "Customer", accessor: (o) => o.customer?.name || "", cell: (o) => (
      <div><div className="font-medium text-neutral-900">{o.customer?.name}</div><div className="text-[11px] text-slate-500">{o.customer?.city || o.customer?.email}</div></div>
    ) },
    { key: "items", header: "Items", hideBelow: "lg", align: "center", accessor: (o) => o.items?.reduce((n, i) => n + (i.quantity || 0), 0) || 0 },
    { key: "total", header: "Total", accessor: (o) => o.total, cell: (o) => <span className="font-semibold">{inr(o.total)}</span> },
    { key: "payment", header: "Payment", hideBelow: "sm", accessor: paidState, cell: (o) => <StatusBadge status={paidState(o) === "paid" ? "delivered" : paidState(o) === "refunded" ? "cancelled" : "pending"} label={paidState(o)} /> },
    { key: "status", header: "Status", accessor: (o) => o.status, cell: (o) => <StatusBadge status={o.status} /> },
    { key: "actions", header: "Update", sortable: false, align: "right", cell: (o) => (
      <select value={o.status} onClick={(e) => e.stopPropagation()} onChange={(e) => changeStatus(o.id, e.target.value)} data-testid={`order-status-${o.id}`}
        className={`${inputCls} !h-8 !w-[130px] !text-[12px]`} aria-label={`Change status for ${o.order_number}`}>
        {STATUSES.map((s) => <option key={s} value={s}>{pretty(s)}</option>)}
      </select>
    ) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const filters = useMemo(() => [
    { key: "status", label: "All statuses", accessor: (o) => o.status, options: STATUSES.map((s) => ({ value: s, label: pretty(s) })) },
    { key: "payment", label: "All payments", accessor: paidState },
    { key: "method", label: "All methods", accessor: (o) => o.payment_method || "online" },
    { key: "fulfil", label: "All fulfilment", accessor: (o) => o.fulfillment_type || "delivery" },
  ], []);

  const openPrescription = async (order, p) => {
    try {
      const res = await fetch(`${API}/vendor/orders/${order.id}/prescription/${p.file}`, { headers: { Authorization: `Bearer ${localStorage.getItem("s24_token")}` } });
      if (!res.ok) throw new Error();
      window.open(URL.createObjectURL(await res.blob()), "_blank", "noopener");
    } catch { toast.error("Could not open the prescription."); }
  };

  return (
    <div className="space-y-4" data-testid="vendor-orders">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={pageTitle}>Orders</h1>
          <p className={pageSub}>{orders.length} total orders. Click a row for details.</p>
        </div>
        <Link to="/vendor/orders/new" data-testid="create-order-btn" className={btnPrimary}><Plus size={15} /> Create order</Link>
      </div>

      <DataTable testId="orders-table" exportName="orders" columns={columns} data={orders} loading={loading} rowKey="id"
        searchKeys={["order_number", (o) => o.customer?.name, (o) => o.customer?.email, (o) => o.customer?.mobile]} searchPlaceholder="Search order no., customer, email, mobile…"
        filters={filters} dateField={{ label: "Order date", accessor: (o) => o.created_at }} defaultSort={{ key: "created_at", dir: "desc" }}
        onRowClick={setSel} rowTestId={(o) => `order-row-${o.id}`} emptyIcon={ShoppingBag} emptyTitle="No orders yet" emptyText="Orders from your storefront will show up here." />

      <Modal open={!!sel} onClose={() => setSel(null)} size="lg" title={sel ? `Order ${sel.order_number}` : ""} subtitle={sel ? `Placed ${(sel.created_at || "").slice(0, 16).replace("T", " ")}` : ""}
        footer={sel && <>
          <button onClick={() => invoice(sel)} data-testid="order-invoice-btn" className={btnOutline}><FileText size={14} /> Invoice</button>
          <button onClick={() => whatsapp(sel)} data-testid="order-whatsapp-btn" className={`${btn} border border-green-200 bg-green-50 text-green-700 hover:bg-green-100`}><MessageCircle size={14} /> WhatsApp</button>
          <button onClick={() => refund(sel)} disabled={sel.payment_status === "refunded"} data-testid="order-refund-btn" className={`${btn} border border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}><RotateCcw size={14} /> Refund</button>
        </>}>
        {sel && (
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
            <div className="space-y-4">
              <section>
                <div className="flex items-center justify-between mb-1.5"><h3 className="text-[12px] font-semibold text-slate-500">Customer</h3><StatusBadge status={sel.status} /></div>
                <dl className="grid grid-cols-[70px_1fr] gap-y-1 text-neutral-800">
                  <dt className="text-slate-500">Name</dt><dd>{sel.customer?.name}</dd>
                  <dt className="text-slate-500">Email</dt><dd className="break-all">{sel.customer?.email}</dd>
                  <dt className="text-slate-500">Mobile</dt><dd>{sel.customer?.mobile}</dd>
                  <dt className="text-slate-500">Address</dt><dd>{[sel.customer?.address, sel.customer?.city].filter(Boolean).join(", ") || "—"}</dd>
                  {sel.delivery_date && <><dt className="text-slate-500">Deliver on</dt><dd data-testid="order-delivery-slot">{sel.delivery_date}{sel.time_slot?.label ? ` · ${sel.time_slot.label}` : ""}</dd></>}
                </dl>
              </section>
              <section>
                <h3 className="text-[12px] font-semibold text-slate-500 mb-1.5">Items</h3>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {sel.items?.map((it, i) => (
                    <div key={i} className="flex justify-between gap-3 px-3 py-2"><span>{it.name} × {it.quantity}{it.variant ? ` (${it.variant})` : ""}</span><span className="font-medium">{inr(it.price * it.quantity)}</span></div>
                  ))}
                </div>
              </section>
            </div>
            <div className="space-y-4">
              <section>
                <h3 className="text-[12px] font-semibold text-slate-500 mb-1.5">Summary</h3>
                <div className="space-y-1">
                  <Row l="Subtotal" v={inr(sel.subtotal)} />
                  {sel.discount ? <Row l="Discount" v={"-" + inr(sel.discount)} /> : null}
                  <Row l={`Tax (${sel.tax_label || "GST"})`} v={inr(sel.tax || 0)} />
                  <Row l="Shipping" v={inr(sel.shipping)} />
                  <div className="flex justify-between font-bold text-green-700 pt-1.5 border-t border-slate-200 mt-1.5"><span>Total</span><span>{inr(sel.total)}</span></div>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600">
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 capitalize">{pretty(sel.fulfillment_type || "delivery")}</span>
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 uppercase">{sel.payment_method || "online"}</span>
                  <span className="px-2 py-0.5 rounded-md bg-slate-100 capitalize">{sel.payment_status}</span>
                </div>
              </section>
              {sel.prescriptions?.length > 0 && (
                <section data-testid="order-prescriptions">
                  <h3 className="text-[12px] font-semibold text-slate-500 mb-1.5">Doctor&apos;s prescription</h3>
                  <div className="space-y-1.5">
                    {sel.prescriptions.map((p) => (
                      <button key={p.file} onClick={() => openPrescription(sel, p)} className="w-full flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-left text-blue-700 hover:bg-blue-100">
                        <FileText size={14} /><span className="truncate">{p.name}</span><span className="ml-auto text-[11px]">View</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
              {sel.status_history?.length > 0 && (
                <section>
                  <h3 className="text-[12px] font-semibold text-slate-500 mb-1.5">Timeline</h3>
                  <div className="space-y-1.5">
                    {sel.status_history.map((h, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-orange-500" />
                        <span className="capitalize font-medium">{pretty(h.status)}</span>
                        <span className="text-[11px] text-slate-500 ml-auto font-mono">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
const Row = ({ l, v }) => <div className="flex justify-between text-neutral-800"><span className="text-slate-500">{l}</span><span>{v}</span></div>;
